import React, { FC, useEffect, useMemo } from 'react';
import { Chip, Field, Icon } from '@kontentino/ui';
import PostComposerPlanningContentPlanSelect from 'app/modules/posts/modules/post/components/composer/postComposerPlanning/PostComposerPlanningContentPlanSelect';
import PostComposerPlanningContentTimeInput from 'app/modules/posts/modules/post/components/composer/postComposerPlanning/PostComposerPlanningContentTimeInput';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { usePostComposer } from './PostComposerProvider';
import SingleDatePicker from 'app/components/datepicker-dropdown/SingleDatePicker';
import { PageDetail } from 'types/Page';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import useBoolean from 'utils/hooks/useBoolean';
import { useTranslation } from 'react-i18next';

type Props = {
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
};

const PostComposerPlanning: FC<Props> = ({ accordionProps }) => {
  const { form, setForm } = usePostComposer();
  const page = form.page as PageDetail;
  const hasProjectValue = Boolean(form.project?.id);
  const defaultProject = form.id
    ? null
    : page?.projects?.find(
        (project) => project.name?.toLowerCase() === 'default',
      );
  const isDefaultProject = form.id
    ? form.project?.name?.toLowerCase() === 'default'
    : form.project?.id === defaultProject?.id;
  const { t } = useTranslation();
  const [
    showContentPlanSelect,
    {
      on: onShowContentPlanSelect,
      off: onHideContentPlanSelect,
      toggle: toggleContentPlanSelect,
    },
  ] = useBoolean(false);

  useEffect(() => {
    if (form.id) {
      isDefaultProject ? onHideContentPlanSelect() : onShowContentPlanSelect();
    } else {
      onHideContentPlanSelect();
      if (!hasProjectValue) {
        if (defaultProject) {
          setForm({
            project: {
              id: defaultProject.id,
            },
          });
        } else if (page?.projects?.length > 0) {
          setForm({
            project: {
              id: page.projects[0].id,
            },
          });
        }
      }
    }
  }, [
    form.id,
    hasProjectValue,
    isDefaultProject,
    defaultProject,
    page?.projects,
    setForm,
    onShowContentPlanSelect,
    onHideContentPlanSelect,
  ]);

  const shouldShowContentPlanSelect = showContentPlanSelect;

  return useMemo(
    () => (
      <AccordionWidget
        title={<AccordionWidgetTitle variant="postPlanning" />}
        titleSuffix={(isOpen) =>
          !isOpen &&
          form.date &&
          form.time &&
          form.project?.id && (
            <Chip variant="grey">
              <Icon icon={faCheck} size="sm" />
            </Chip>
          )
        }
        icon={<AccordionWidgetIcon variant="postPlanning" />}
        data-name="posts-composer_post-planning_date-time"
        {...accordionProps}
      >
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-gap-3">
            <Field.Group className="tw-flex-1">
              <Field.Label required>{t('date')}</Field.Label>
              <UserPermissionGate
                scopes={UserPermission.POST_EDIT_DATE_TIME}
                deniedProps={{ isDisabled: true }}
              >
                <SingleDatePicker
                  clearButtonTooltipContent={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('composer.postsWithoutDateTooltip'),
                      }}
                    />
                  }
                  date={form.date}
                  onChange={(date) => {
                    setForm({
                      date,
                    });
                  }}
                  withClearButton
                  dataCy={{
                    placeholder: 'post-form-single-date-picker',
                    wrapper: 'post-form-single-date-picker-wrapper',
                  }}
                />
              </UserPermissionGate>
            </Field.Group>
            <Field.Group className="tw-flex-1">
              <Field.Label required>{t('composer.time')}</Field.Label>
              <UserPermissionGate
                scopes={UserPermission.POST_EDIT_DATE_TIME}
                deniedProps={{ disabled: true }}
              >
                <PostComposerPlanningContentTimeInput
                  pageId={form.page.id}
                  onChange={(time) => setForm({ time })}
                  value={form.time}
                />
              </UserPermissionGate>
            </Field.Group>
          </div>
          <Field.Group>
            <div
              className={
                'tw-group tw-flex tw-items-center tw-gap-2 hover:tw-cursor-pointer '
              }
              onClick={toggleContentPlanSelect}
            >
              <Field.Label
                required
                className={
                  'hover:tw-cursor-pointer  group-hover:tw-text-primary-100'
                }
              >
                {t('composer.assignToContentPlan')}
              </Field.Label>

              <Icon
                icon={shouldShowContentPlanSelect ? faChevronUp : faChevronDown}
                size="sm"
                className="tw-text-grayscale-100  group-hover:tw-text-primary-100"
              />
            </div>
            <UserPermissionGate
              scopes={UserPermission.POST_EDIT_PROJECT}
              deniedProps={{ disabled: true }}
            >
              <>
                {shouldShowContentPlanSelect && (
                  <PostComposerPlanningContentPlanSelect
                    pageId={form.page.id}
                    onChange={(contentPlanId) =>
                      setForm({
                        project:
                          typeof contentPlanId === 'number'
                            ? {
                                id: contentPlanId,
                              }
                            : null,
                      })
                    }
                    value={form.project?.id ?? null}
                  />
                )}
              </>
            </UserPermissionGate>
          </Field.Group>
        </div>
      </AccordionWidget>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      form.date,
      form.time,
      form.project?.id,
      form.page.id,
      accordionProps,
      shouldShowContentPlanSelect,
    ],
  );
};

export default PostComposerPlanning;
